// Core API - Centralized Error Handler
// Location: src/core/api/errorHandler.ts

import { AxiosError } from 'axios';
import { ApiError, ErrorCode } from '../../types/api.types';

const ERROR_MESSAGES: Record<ErrorCode, string> = {
  [ErrorCode.NETWORK_ERROR]: 'Unable to connect. Please check your internet connection.',
  [ErrorCode.TIMEOUT]: 'Request timed out. Please try again.',
  [ErrorCode.UNAUTHORIZED]: 'Session expired. Please login again.',
  [ErrorCode.FORBIDDEN]: 'You do not have permission to perform this action.',
  [ErrorCode.NOT_FOUND]: 'The requested resource was not found.',
  [ErrorCode.VALIDATION_ERROR]: 'Please check your input and try again.',
  [ErrorCode.SERVER_ERROR]: 'Something went wrong on our end. Please try again later.',
  [ErrorCode.UNKNOWN]: 'An unexpected error occurred.',
};

export const handleApiError = (error: AxiosError): ApiError => {
  // Network error (no response from server)
  if (!error.response) {
    if (error.code === 'ECONNABORTED') {
      return new ApiError(
        ERROR_MESSAGES[ErrorCode.TIMEOUT],
        0,
        ErrorCode.TIMEOUT
      );
    }
    return new ApiError(
      ERROR_MESSAGES[ErrorCode.NETWORK_ERROR],
      0,
      ErrorCode.NETWORK_ERROR
    );
  }

  const { status, data } = error.response as { status: number; data: any };

  switch (status) {
    case 400:
      return new ApiError(
        data.message || ERROR_MESSAGES[ErrorCode.VALIDATION_ERROR],
        400,
        ErrorCode.VALIDATION_ERROR,
        data.errors || data.details
      );

    case 401:
      return new ApiError(
        data.message || ERROR_MESSAGES[ErrorCode.UNAUTHORIZED],
        401,
        ErrorCode.UNAUTHORIZED
      );

    case 403:
      return new ApiError(
        data.message || ERROR_MESSAGES[ErrorCode.FORBIDDEN],
        403,
        ErrorCode.FORBIDDEN
      );

    case 404:
      return new ApiError(
        data.message || ERROR_MESSAGES[ErrorCode.NOT_FOUND],
        404,
        ErrorCode.NOT_FOUND
      );

    case 429:
      return new ApiError(
        data?.message || 'Too many requests. Please wait a moment and try again.',
        429,
        ErrorCode.UNKNOWN
      );

    case 500:
    case 502:
    case 503:
    case 504:
      return new ApiError(
        ERROR_MESSAGES[ErrorCode.SERVER_ERROR],
        status,
        ErrorCode.SERVER_ERROR
      );

    default:
      return new ApiError(
        data.message || ERROR_MESSAGES[ErrorCode.UNKNOWN],
        status,
        ErrorCode.UNKNOWN,
        data
      );
  }
};

// Retry logic for failed requests
export const retryRequest = async <T>(
  fn: () => Promise<T>,
  retries: number = 3,
  delay: number = 1000
): Promise<T> => {
  try {
    return await fn();
  } catch (error) {
    const apiError = error as ApiError;

    // Don't retry for client errors (4xx) or if no retries left
    if (
      retries === 0 ||
      (apiError.status >= 400 && apiError.status < 500)
    ) {
      throw error;
    }

    // Wait before retrying (exponential backoff)
    await new Promise(resolve => setTimeout(resolve, delay));

    // Retry with increased delay
    return retryRequest(fn, retries - 1, delay * 2);
  }
};
